import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import { truncateText } from '../utils/helpers';
import ImageWithFallback from './ImageWithFallback';

const DestinationCard = ({ destination }) => {
  const { isFavorite, addToFavorites, removeFromFavorites } = useContext(ThemeContext);
  const favorite = isFavorite(destination.slug);

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (favorite) {
      removeFromFavorites(destination.slug);
    } else {
      addToFavorites(destination);
    }
  };

  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="card group"
    >
      <Link to={`/destinations/${destination.slug}`} className="block">
        {/* Image */}
        <div className="relative overflow-hidden">
          <ImageWithFallback
            src={destination.images[0]}
            alt={destination.name}
            className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
            width={400}
            height={250}
          />
          <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all duration-300" />
          
          {/* Favorite Button */}
          <button
            onClick={handleFavoriteClick}
            className="absolute top-3 right-3 p-2 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-all duration-200"
            aria-label={favorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <svg
              className={`w-5 h-5 ${favorite ? 'text-red-500 fill-current' : 'text-secondary-400'}`}
              fill={favorite ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            {destination.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Title and Country */}
          <h3 className="text-xl font-bold text-secondary-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200">
            {destination.name}
          </h3>
          <p className="text-secondary-600 dark:text-secondary-400 text-sm mb-3">
            {destination.country}
          </p>

          {/* Excerpt */}
          <p className="text-secondary-700 dark:text-secondary-300 text-sm leading-relaxed mb-4">
            {truncateText(destination.excerpt, 120)}
          </p>

          {/* View Link */}
          <div className="flex items-center text-primary-600 dark:text-primary-400 font-medium text-sm group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors duration-200">
            <span>View Destination</span>
            <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default DestinationCard;
